import { Link, useParams } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import { useSeo } from '@/hooks/useSeo';
import ArticleAside from './components/ArticleAside';

export default function ArticlePage() {
  const { id = '' } = useParams();
  const article = articles.find((a) => a.id === id);
  const category = categories.find((c) => c.slug === article?.category);

  const related = article
    ? articles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 3)
    : [];

  const toc = article
    ? article.sections.filter((s) => s.heading).map((s, i) => ({ id: `section-${i}`, label: s.heading as string }))
    : [];

  useSeo({
    title: article ? `${article.title} - 17爆料网` : '内容未找到 - 17爆料网',
    description: article ? article.excerpt : '17爆料网内容页',
    keywords: article ? `17爆料网,${article.categoryName},${article.tags.join(',')}` : '17爆料网',
    path: `/article/${id}`,
    type: 'article',
    jsonLd: article
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: [article.cover],
            datePublished: article.date,
            dateModified: article.date,
            articleSection: article.categoryName,
            keywords: article.tags.join(','),
            author: { '@type': 'Organization', name: '17爆料网' },
            publisher: { '@type': 'Organization', name: '17爆料网' },
            mainEntityOfPage: `https://dyfscl.cn/article/${article.id}`,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://dyfscl.cn/' },
              {
                '@type': 'ListItem',
                position: 2,
                name: article.categoryName,
                item: `https://dyfscl.cn/category/${article.category}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.title,
                item: `https://dyfscl.cn/article/${article.id}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!article) {
    return (
      <div className="px-4 md:px-8 py-20 text-center">
        <h1 className="font-heading font-bold text-2xl text-foreground-950">这条线索不存在或已被调整</h1>
        <p className="mt-3 text-sm text-foreground-600">可能链接有误，你可以回到对应的栏目继续浏览。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  let headingIndex = -1;

  return (
    <>
      <article>
        <header className="bg-background-100 border-b border-background-200">
          <div className="px-4 md:px-8 pt-6 pb-10">
            <Breadcrumbs
              items={[{ label: article.categoryName, to: `/category/${article.category}` }, { label: '正文' }]}
            />
            <div className="mt-6 max-w-3xl">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="px-2.5 py-1 rounded bg-accent-600 text-background-50 text-xs font-medium">独家整理</span>
                <Link
                  to={`/category/${article.category}`}
                  className="px-2.5 py-1 rounded bg-secondary-100 text-secondary-800 text-xs hover:bg-secondary-200 transition-colors cursor-pointer"
                >
                  {article.categoryName}
                </Link>
                <span className="text-xs text-foreground-500">{article.date}</span>
              </div>
              <h1 className="font-heading font-bold text-2xl md:text-4xl leading-tight text-foreground-950">
                {article.title}
              </h1>
              <p className="mt-4 text-base text-foreground-600 leading-relaxed">{article.excerpt}</p>
              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author} · {article.role}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>阅读约 {article.readTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {article.views} 次阅读
                </span>
              </div>
            </div>
          </div>
        </header>

        <div className="px-4 md:px-8 py-10">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <figure className="mb-8">
                <div className="w-full h-[260px] md:h-[400px] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={article.cover}
                    alt={`${article.title} - 17爆料网独家首发配图`}
                    title={`${article.title} - 17爆料网`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <figcaption className="mt-3 text-xs text-foreground-500">
                  图为事件相关场景示意图，仅用于内容呈现 · 17爆料网
                </figcaption>
              </figure>

              <div className="article-body text-foreground-800 text-[15px]">
                {article.sections.map((s, i) => {
                  if (s.heading) headingIndex += 1;
                  const anchor = s.heading ? `section-${headingIndex}` : undefined;
                  return (
                    <section key={i} className="mb-2">
                      {s.heading && (
                        <h2 id={anchor} className="scroll-mt-28">
                          {s.heading}
                        </h2>
                      )}
                      {s.paragraphs.map((p, pi) => (
                        <p key={pi}>{p}</p>
                      ))}
                      {s.list && (
                        <ul className="my-5 space-y-2.5 pl-1">
                          {s.list.map((li, li2) => (
                            <li key={li2} className="flex items-start gap-3 text-[15px] leading-relaxed">
                              <i className="ri-checkbox-blank-circle-fill text-[7px] text-primary-500 mt-2.5"></i>
                              <span>{li}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {s.quote && (
                        <blockquote className="my-6 pl-5 border-l-2 border-accent-500 bg-background-100 py-3 pr-4 rounded-r-md">
                          <p className="!mb-0 text-foreground-700 italic">{s.quote}</p>
                        </blockquote>
                      )}
                    </section>
                  );
                })}
              </div>

              <div className="mt-8 pt-6 border-t border-background-200 flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500 mr-1">相关标签：</span>
                {article.tags.map((t) => (
                  <span key={t} className="px-3 py-1 rounded-full bg-background-100 text-foreground-700 text-xs">
                    #{t}
                  </span>
                ))}
              </div>

              <div className="mt-6 p-5 rounded-lg bg-background-100 border border-background-200 text-xs text-foreground-500 leading-relaxed">
                <strong className="text-foreground-700">核实说明：</strong>
                本文基于多个信源交叉整理，已对非公开当事人做匿名化处理。事件如有实质进展，我们会在原文更新并标注时间。
              </div>
            </div>

            <ArticleAside toc={toc} related={related} />
          </div>
        </div>
      </article>

      {category && (
        <section className="bg-background-100 border-t border-background-200">
          <div className="px-4 md:px-8 py-12">
            <div className="flex items-end justify-between gap-4 mb-6">
              <div>
                <span className="text-xs tracking-widest text-primary-700 font-label">MORE FROM {category.en}</span>
                <h2 className="mt-2 font-heading font-bold text-xl md:text-2xl text-foreground-950">
                  继续浏览{category.name}
                </h2>
              </div>
              <Link
                to={`/category/${category.slug}`}
                className="shrink-0 inline-flex items-center gap-1 text-sm text-primary-700 hover:text-primary-800 cursor-pointer whitespace-nowrap"
              >
                进入栏目
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {related.map((a) => (
                <Link
                  key={a.id}
                  to={`/article/${a.id}`}
                  className="group block p-5 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <span className="text-xs text-foreground-500">{a.date}</span>
                  <h3 className="mt-2 font-heading font-bold text-base text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
                    {a.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">{a.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}